import { forwardRef } from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';

import MenuItem from '../MenuItem';
import Typography, { TypographyVariant } from '../Typography';

import { SelectProps, Variant, Theme, SelectMenuItemThemeMap } from './Select.types';
import { StyledFormControl, StyledSelect } from './Select.theme';

const SelectComponent = forwardRef<HTMLDivElement, SelectProps<any>>((props, ref) => {
  const {
    id,
    name,
    className,
    style,
    menuItemClassName,
    menuItemStyle,
    formControlClassName,
    value,
    startAdornmentNode,
    renderValue,
    defaultValue,
    inputElement,
    menuItems=[],
    renderMenuItem,
    getMenuItemValue,
    getMenuItemLabel,
    isMenuItemSelected,
    isMenuItemDisabled,
    onChange,
    onClose,
    onOpen,
    variant,
    theme,
    open,
    autoWidth,
    label,
    menuProps,
    multiple,
    iconComponent,
    formControlTheme,
    disabled,
    formControlError,
    focused,
    fullWidth,
    hiddenFormLabel,
    margin,
    required,
    formControlSize,
    formControlVariant,
    showLabel,
    inputLabel,
    labelShrink,
    showHelperText,
    formHelperText,
  } = props;

  let menuItemRenderer: (menuItem: any, index: number) => any = () => null;
  if (renderMenuItem) menuItemRenderer = renderMenuItem;
  else if (getMenuItemValue && getMenuItemLabel) menuItemRenderer = (menuItem: any, index: number) => (
    <MenuItem
      id={`${id}-Menu-Item-${index + 1}`}
      key={`Menu-Item-${index + 1}`}
      className={menuItemClassName}
      style={menuItemStyle}
      theme={SelectMenuItemThemeMap[theme ?? Theme.light]}
      value={getMenuItemValue(menuItem)}
      selected={!!isMenuItemSelected?.(menuItem)}
      disabled={!!isMenuItemDisabled?.(menuItem)}
    >
      {getMenuItemLabel(menuItem)}
    </MenuItem>
  );

  const adornmentNode = (
      <InputAdornment position="start">
        {startAdornmentNode}
      </InputAdornment>
  );

  return (
    <StyledFormControl
      id={`${id}-Form-Control`}
      ref={ref}
      styledFormControlClass={formControlClassName}
      color={formControlTheme}
      disabled={disabled}
      error={formControlError}
      focused={focused}
      fullWidth={fullWidth}
      hiddenLabel={hiddenFormLabel}
      margin={margin}
      required={required}
      size={formControlSize}
      variant={formControlVariant}
    >
      {showLabel && (
        <InputLabel id={`${id}-Input-Label`} shrink={labelShrink}>
          {inputLabel}
        </InputLabel>
      )}
      <StyledSelect
        id={id}
        name={name}
        styledSelectClass={className}
        formControlTheme={theme}
        style={style}
        input={inputElement as any}
        labelId={`${id}-Input-Label`}
        value={value}
        defaultValue={defaultValue}
        renderValue={renderValue}
        open={open}
        onChange={onChange}
        onClose={onClose}
        onOpen={onOpen}
        autoWidth={autoWidth}
        IconComponent={iconComponent}
        label={label}
        MenuProps={{
          ...menuProps,
        }}
        multiple={multiple}
        variant={variant}
        startAdornment={startAdornmentNode ? adornmentNode : null}
      >
        {menuItems?.length > 0 ? menuItems?.map(menuItemRenderer) : <Typography.P2 variant={TypographyVariant.active} style={{ color: '#FFFFFF', marginLeft: '12px' }}>No data available</Typography.P2>}
      </StyledSelect>
      {showHelperText && <FormHelperText id={`${id}-Form-Helper-Text`}>{formHelperText}</FormHelperText>}
    </StyledFormControl>
  );
});

SelectComponent.defaultProps = {
  name: undefined,
  className: undefined,
  style: undefined,
  onChange: () => {},
  onClose: () => {},
  onOpen: () => {},
  value: '',
  defaultValue: '',
  variant: Variant.standard,
  theme: Theme.light,
  showLabel: false,
  showHelperText: false,
  formHelperText: 'Helper Text',
  inputLabel: 'Input Label',
  menuItems: [],
  menuProps: undefined,
  startAdornmentNode: undefined,
};

export {
  Theme as SelectTheme,
  Variant as SelectVariant,
  Margin as SelectMargin,
} from './Select.types';

export default SelectComponent;
